import { Input, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useRef, useState } from 'react';

// Input params = a flat Record<string,string>. Template-defined keys render as labelled inputs; any
// extra keys (or edit-mode existing keys not in the schema) render as editable mono key/value rows.
// Rows are held locally with stable ids so editing a key doesn't reshuffle inputs; every change
// recomputes the record and lifts it via `onChange` (blank keys are kept locally but not committed).
// Hosts must remount this component when the record is reseeded from outside (reset, template switch).
export default function InputParamsSection({ template, inputParams, onChange }) {
	const params = inputParams || {};

	const schema = template?.inputSchemaV2;
	const required = schema?.requiredFields || [];
	const optional = schema?.optionalFields || [];
	const schemaFields = [...required.map((f) => [f, true]), ...optional.map((f) => [f, false])];
	const schemaKeys = new Set(schemaFields.map(([f]) => f.key));

	const seq = useRef(0);
	const nextId = () => (seq.current += 1);
	const lastAddedRef = useRef(null);
	const [rows, setRows] = useState(() =>
		Object.keys(params)
			.filter((k) => !schemaKeys.has(k))
			.map((key) => ({ id: nextId(), key, value: params[key] }))
	);

	const setSchemaValue = (key, value) => onChange({ ...params, [key]: value });

	const commit = (nextRows) => {
		setRows(nextRows);
		const next = {};
		for (const [f] of schemaFields) {
			if (params[f.key] !== undefined) next[f.key] = params[f.key];
		}
		for (const { key, value } of nextRows) {
			const k = String(key).trim();
			// blank rows stay local-only; free rows must never shadow a template-defined field
			if (!k || schemaKeys.has(k)) continue;
			next[k] = value;
		}
		onChange(next);
	};

	const updateRow = (id, partial) => commit(rows.map((r) => (r.id === id ? { ...r, ...partial } : r)));
	const removeRow = (id) => commit(rows.filter((r) => r.id !== id));
	const addRow = () => {
		const id = nextId();
		lastAddedRef.current = id;
		commit([...rows, { id, key: '', value: '' }]);
	};

	return (
		<>
			{schemaFields.length > 0 && (
				<div className="ei-fgrid" style={{ marginBottom: 20 }}>
					{schemaFields.map(([f, isRequired]) => (
						<div className="ei-fitem" key={f.key}>
							<span className="ei-flabel">
								{f.label || f.key}
								{isRequired ? <span className="ei-field-req"> *</span> : null}
							</span>
							<Input
								value={params[f.key] || ''}
								placeholder={f.description || `Enter ${f.label || f.key}`}
								onChange={(e) => setSchemaValue(f.key, e.target.value)}
							/>
							{f.description ? <span className="ei-field-hint">{f.description}</span> : null}
						</div>
					))}
				</div>
			)}

			<div className={`ei-minilabel${schemaFields.length > 0 ? ' has-rule' : ''}`}>Additional parameters</div>

			{rows.map((row) => (
				<div className="ei-kv-row" key={row.id}>
					<div className="js-token" style={{ flex: '0 0 220px' }}>
						<Input
							value={row.key}
							onChange={(e) => updateRow(row.id, { key: e.target.value })}
							placeholder="key"
							autoFocus={row.id === lastAddedRef.current}
							status={schemaKeys.has(String(row.key).trim()) ? 'error' : undefined}
							title={
								schemaKeys.has(String(row.key).trim())
									? 'Defined by the template above — edit it there'
									: undefined
							}
						/>
					</div>
					<div className="js-token" style={{ flex: 1, minWidth: 0 }}>
						<Input
							value={row.value == null ? '' : String(row.value)}
							onChange={(e) => updateRow(row.id, { value: e.target.value })}
							placeholder="value"
							title={row.value == null ? undefined : String(row.value)}
						/>
					</div>
					<Tooltip title="Remove">
						<button
							type="button"
							className="js-iconbtn js-danger"
							onClick={() => removeRow(row.id)}
							aria-label="Remove parameter"
						>
							<DeleteOutlined />
						</button>
					</Tooltip>
				</div>
			))}

			<div style={{ marginTop: 4 }}>
				<button type="button" className="js-linkbtn" onClick={addRow}>
					<PlusOutlined /> Add parameter
				</button>
			</div>
		</>
	);
}
